import { useState, useEffect, useRef } from 'react';
import { cachedFetch } from '../utils/footerCache';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
interface FooterSettingsData {
    [key: string]: string | boolean;
}
interface UseFooterSettingsReturn {
    settings: FooterSettingsData;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}
interface FooterSettingsResponse {
    success: boolean;
    data?: FooterSettingsData;
    message?: string;
}
const FALLBACK_FOOTER_SETTINGS: FooterSettingsData = {
    footer_description: 'Empowering law enforcement professionals with comprehensive training solutions and modern event management tools.',
    footer_phone: '(555) 123-4567',
    footer_email: 'info@americanpolicetraining.com',
    footer_location: 'Washington, DC',
    footer_copyright_text: '© 2025 American Police Training. All rights reserved.',
    footer_show_contact_info: true,
    footer_show_social_links: true,
};

export const useFooterSettings = (): UseFooterSettingsReturn => {
    // Seed defaults immediately so description/copy do not pop in after fetch (footer CLS).
    const [settings, setSettings] = useState<FooterSettingsData>(FALLBACK_FOOTER_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);
    const fallbackSettings = FALLBACK_FOOTER_SETTINGS;
    const fetchSettings = async () => {
        if (!isMountedRef.current) {
            return;
        }
        let softTimeoutTriggered = false;
        const handleSoftTimeout = () => {
            if (!isMountedRef.current || softTimeoutTriggered) {
                return;
            }
            softTimeoutTriggered = true;
            logger.api('Footer settings request taking longer than expected, using defaults until response arrives', "GET");
            setSettings(fallbackSettings);
            setLoading(false);
        };
        try {
            setLoading(true);
            setError(null);
            const result = await cachedFetch<FooterSettingsResponse>('/api/footer-settings/frontend', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            }, undefined, {
                softTimeoutMs: 8000,
                onSoftTimeout: handleSoftTimeout
            });
            if (!isMountedRef.current) {
                return;
            }
            if (result.success && result.data) {
                setSettings(result.data);
            }
            else {
                throw new Error(result.message || 'Failed to fetch footer settings');
            }
        }
        catch (err: any) {
            if (!isMountedRef.current) {
                return;
            }
            if (!softTimeoutTriggered) {
                logger.error('Error fetching footer settings (using defaults):', err, {});
                setSettings(fallbackSettings);
            }
            setError(null); // Don't show error to user since we have defaults
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };
    const refetch = async () => {
        await fetchSettings();
    };
    useEffect(() => {
        isMountedRef.current = true;
        // Use setTimeout to make this non-blocking
        const timeoutId = setTimeout(() => {
            void fetchSettings();
        }, 50);
        // Cleanup function to abort any ongoing request
        return () => {
            clearTimeout(timeoutId);
            isMountedRef.current = false;
        };
    }, []);
    return {
        settings,
        loading,
        error,
        refetch,
    };
};
